import { Controller, FieldValues, useForm } from 'react-hook-form'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  useAutocompleteTitleOptionsLazyQuery,
  useCreditOptionsQuery,
  useCustomerAutocompleteOptionsLazyQuery,
} from '@kistill/client/data-access'
import { ListViewContext } from '../../../contexts/ListViewContext'
import { FilterControls } from './FilterControls'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface CreditFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function CreditFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: CreditFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('credit', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset } = useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)

  const [inputValueCustomer, setInputValueCustomer] = useState('')
  const [inputValueTitle, setInputValueTitle] = useState('')
  const [customerOptions, setCustomerOptions] = useState<SelectItem[]>([])
  const [titleOptions, setTitleOptions] = useState<SelectItem[]>([])

  const { data } = useCreditOptionsQuery()
  const creditOptions = data?.creditOptions as SelectItem[]

  const [getCustomerOptions] = useCustomerAutocompleteOptionsLazyQuery({
    variables: { query: inputValueCustomer },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.customerOptions) {
        setCustomerOptions(
          data.customerOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const [getTitleOptions] = useAutocompleteTitleOptionsLazyQuery({
    variables: { query: inputValueTitle },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.titleOptions) {
        setTitleOptions(data.titleOptions.slice()) // Use slice() to create a new array
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'credit',
  })

  const handleInputChangeCustomer = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueCustomer(value)
  }

  const handleInputChangeTitle = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueTitle(value)
  }

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
    }
  }
  useEffect(() => {
    getCustomerOptions() // Trigger the query to fetch customer options
  }, [getCustomerOptions])

  useEffect(() => {
    getTitleOptions() // Trigger the query to fetch title options
  }, [getTitleOptions])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'credit',
    columnVisibility,
    setValue,
    clearSpecificFields,
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {!hiddenFields.has('titleSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='titleSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  size='small'
                  multiple
                  options={normalizeSelectItems(titleOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, titleSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  inputValue={inputValueTitle}
                  onInputChange={handleInputChangeTitle}
                  clearOnBlur={false}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Titill' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('tengilidirSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='tengilidirSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(customerOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, tengilidirSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  onInputChange={handleInputChangeCustomer}
                  inputValue={inputValueCustomer}
                  value={value || []}
                  clearOnBlur={false}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Nafn kredits' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('creditTypeSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='creditTypeSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(creditOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, creditTypeSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Tegund kredits' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('comments') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='comments'
              render={({ field }) => (
                <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label='Athugasemdir'
                    fullWidth
                    size='small'
                  />
                </Tooltip>
              )}
            />
          </Grid>
        )}
      </Grid>
      <FilterControls
        selected={selected}
        clearFilters={clearFilters}
        register={register}
        deselectCheckboxChange={deselectCheckboxChange}
      />
    </form>
  )
}
